import type { FC } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import noop from "lodash/noop";

import type { QueryBarItemMode } from "app/components/compare/KeywordsQueryBar/KeywordsQueryBarTypes";
import { DeleteKeywordListConfirmationModal } from "app/components/modals/components/DeleteKeywordListConfirmationModal";
import { i18nFilter } from "app/filters/i18nFilter";
import { useKeywordGroupModalWorkflow } from "app/pages/keyword-analysis/useKeywordGroupModalWorkflow";
import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";
import { AutocompleteDemandAnalysisUnified } from "pro-features/components/AutocompleteDemandAnalysisUnified/AutocompleteDemandAnalysisUnified";
import { AutocompleteKeywordGroups } from "pro-features/components/AutocompleteKeywords/AutocompleteKeywordsGroups";

import { KeywordSearchContainer } from "../KeywordsQueryBarStyles";
import { allTrackers } from "app/services/track/track";

interface IKeywordsQueryBarAutocompleteProps {
    onItemClick: (item) => void;
    selectedValue: string;
    mode: QueryBarItemMode;
    className: string;
    onGroupShareModalOpened?: () => void;
    onGroupShareModalClosed?: () => void;
    onGroupEditorModalOpened?: () => void;
    onGroupEditorModalClosed?: () => void;
    onGroupDelete?: (deletedGroup: { Id: string }) => void;
    showKeywords?: boolean;
    truncateResultsOnBodyClick?: boolean;
    showAutoCreateListCTA?: boolean;
    showGoToMonitorKeywordListButton?: boolean;
    shouldConfirmDeleteKeywordList?: boolean;
    /**
     * NOTE only works when showKeywordGroups is true
     */
    keywordsToFilterFromAutocomplete?: string[];
    keywordsGroupsToFilterFromAutocomplete?: string[];
    onSaveKeywordList?: (IKeywordGroup) => void;
}

interface IStateAndParams {
    country?: number;
}

export const KeywordsQueryBarAutocomplete: FC<IKeywordsQueryBarAutocompleteProps> = (props) => {
    const translate = i18nFilter();
    const {
        onItemClick,
        selectedValue,
        className,
        onGroupShareModalOpened,
        onGroupShareModalClosed,
        onGroupEditorModalOpened,
        onGroupEditorModalClosed,
        onGroupDelete,
        showKeywords = true,
        keywordsToFilterFromAutocomplete,
        keywordsGroupsToFilterFromAutocomplete,
        truncateResultsOnBodyClick,
        showAutoCreateListCTA = true,
        showGoToMonitorKeywordListButton = false,
        shouldConfirmDeleteKeywordList = false,
        mode,
        onSaveKeywordList = noop,
    } = props;
    const stateAndParams = useCurrentStateAndParams<IStateAndParams>();
    const countryId = stateAndParams.params.country;

    const { controller, requestDelete, deleteConfirmationModalProps } = useKeywordGroupModalWorkflow({
        onDeleteSuccess: (deletedGroup) => {
            allTrackers.dispatchTrackEvent("Pop Up", "click", "Create New Keyword Group/Delete");
            onGroupDelete?.(deletedGroup);
        },
    });

    const baseProps = {
        onClick: onItemClick,
        autocompleteProps: {
            placeholder: translate(
                mode === "keywordGroup"
                    ? showKeywords
                        ? "keyword.query.bar.autocomplete.placeholder.text"
                        : "keyword.list.query.bar.autocomplete.placeholder.text"
                    : "keyword.and.keyword.list.query.bar.autocomplete.placeholder.text",
            ),
            isFocused: true,
            autoFocus: true,
            autoSelectInputOnFocus: true,
            preventTruncateUnlessForced: true,
            onCloseClick: onGroupShareModalClosed,
        },
        selectedValue: selectedValue,
        keywordsToFilterFromAutocomplete: keywordsToFilterFromAutocomplete,
        keywordsGroupsToFilterFromAutocomplete: keywordsGroupsToFilterFromAutocomplete,
        onGroupShareModalOpened: onGroupShareModalOpened,
        onGroupShareModalClosed: onGroupShareModalClosed,
        onGroupEditorModalOpened: onGroupEditorModalOpened,
        onGroupEditorModalClosed: onGroupEditorModalClosed,
        onGroupDelete: onGroupDelete,
        truncateResultsOnBodyClick: truncateResultsOnBodyClick,
        showAutoCreateKeywordGroupButton: showAutoCreateListCTA,
        showGoToMonitorKeywordListButton: showGoToMonitorKeywordListButton,
        handleSave: onSaveKeywordList,
    };
    const defaultTab = {
        keyword: "keywords",
        keywordGroup: "keywordGroups",
        searchTerm: "keywords",
        keywordGroupFull: "keywordGroups",
        topic: "topics",
    } as const;
    const defaultSelectedTab = defaultTab[mode];

    const handleBeforeDeleteKeywordGroup = (group: IUserKeywordGroup | undefined) => {
        controller.hide();

        if (!group) {
            return;
        }

        requestDelete(group);
    };

    const renderAutocomplete = () => {
        switch (mode) {
            case "keyword":
            case "keywordGroup":
                return (
                    <>
                        <AutocompleteKeywordGroups
                            {...baseProps}
                            controller={controller}
                            showKeywords={showKeywords}
                            defaultSelectedTab={defaultSelectedTab}
                            onBeforeDeleteKeywordGroup={
                                shouldConfirmDeleteKeywordList ? handleBeforeDeleteKeywordGroup : undefined
                            }
                        />
                        {shouldConfirmDeleteKeywordList ? (
                            <DeleteKeywordListConfirmationModal
                                {...deleteConfirmationModalProps}
                                dataAutomationPrefix="keywords-query-bar-delete-keyword-list-confirmation"
                            />
                        ) : null}
                    </>
                );
            case "searchTerm":
            case "keywordGroupFull":
            case "topic":
                return (
                    <AutocompleteDemandAnalysisUnified
                        onClick={onItemClick}
                        autocompleteProps={baseProps.autocompleteProps}
                        selectedValue={selectedValue}
                        keywordsGroupsToFilterFromAutocomplete={keywordsGroupsToFilterFromAutocomplete}
                        onGroupShareModalOpened={onGroupShareModalOpened}
                        onGroupShareModalClosed={onGroupShareModalClosed}
                        onGroupEditorModalOpened={onGroupEditorModalOpened}
                        onGroupEditorModalClosed={onGroupEditorModalClosed}
                        onGroupDelete={onGroupDelete}
                        truncateResultsOnBodyClick={truncateResultsOnBodyClick}
                        showGoToMonitorKeywordListButton={showGoToMonitorKeywordListButton}
                        shouldConfirmDeleteKeywordList={shouldConfirmDeleteKeywordList}
                        handleSave={onSaveKeywordList}
                        countryId={countryId}
                    />
                );
            default:
                return null;
        }
    };

    return <KeywordSearchContainer className={className}>{renderAutocomplete()}</KeywordSearchContainer>;
};
